import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import {
  ApiFilterOperation,
  EmptyComponent,
  TableCells,
  TableColumn,
  TableColumnAlign,
  TableFilterCells,
  eqFilterParams,
  useStaticListSource,
} from '@eas/common-web';
import { useCustomFilterAutocomplete } from '@composite/autocomplete-item/autocomplete-item-with-deactivated-items';
import { FilterTextCell } from '@composite/filter/filter-cells/filter-text-cell';
import { Redirect } from '@composite/redirect/redirect';
import { StaticDataContext } from '@components/evidence/static-data-provider/static-data-provider';
import { autocompleteColumnMapper } from '@components/form/misc/label-mappers';
import { DeliveredDocument } from '@models';
import { EvidenceBrowserUrl, Role } from '@enums';
import { useJudges } from '../judge/judge-api';
import { useUserRoles } from '../role/role-api';

export function useColumns(): TableColumn<DeliveredDocument>[] {
  const intl = useIntl();

  const { agendas, documentDeliveryMethods } = useContext(StaticDataContext);
  const useAgendas = () => useStaticListSource(agendas);
  const useDeliveryMethods = () => useStaticListSource(documentDeliveryMethods);
  const useIndexes = () =>
    useUserRoles({
      ...eqFilterParams({ field: 'role.id', value: Role.INDEX }),
      include: ['DEACTIVATED', 'DELETED'],
    });

  return [
    {
      datakey: 'id',
      name: intl.formatMessage({
        id: 'ES__DELIVERED_DOCUMENTS_PROCESSED__TABLE__COLUMN__REDIRECT',
        defaultMessage: 'Přesměrování',
      }),
      width: 50,
      NameComponent: EmptyComponent,
      CellComponent: function Cell(props) {
        return (
          <>
            <Redirect
              url={EvidenceBrowserUrl.DOCUMENTS_DELIVERED_PROCESSED}
              id={props.value}
              showRedirectInCurrent={false}
            />
          </>
        );
      },
      align: TableColumnAlign.LEFT,
    },
    {
      datakey: 'deliveryDate',
      name: intl.formatMessage({
        id: 'ES__DELIVERED_DOCUMENTS_PROCESSED__TABLE__COLUMN__DELIVERY_DATE',
        defaultMessage: 'Datum doručení',
      }),
      width: 150,
      CellComponent: TableCells.DateCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'jid',
      name: intl.formatMessage({
        id: 'ES__DELIVERED_DOCUMENTS_PROCESSED__TABLE__COLUMN__JID',
        defaultMessage: 'JID',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'referenceNumber',
      name: intl.formatMessage({
        id: 'ES__DELIVERED_DOCUMENTS_PROCESSED__TABLE__COLUMN__REFERENCE_NUMBER',
        defaultMessage: 'Číslo jednací',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      sortkey: 'referenceNumberSort',
      filterable: true,
    },
    {
      datakey: 'esFile.number.value',
      sortkey: 'esFile.number.valueSort',
      name: intl.formatMessage({
        id: 'ES__DELIVERED_DOCUMENTS_PROCESSED__TABLE__COLUMN__ASSIGNED_FILE_NUMBER',
        defaultMessage: 'Spisová značka',
      }),
      width: 120,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'summary',
      name: intl.formatMessage({
        id: 'ES__DELIVERED_DOCUMENTS_PROCESSED__TABLE__COLUMN__SUMMARY',
        defaultMessage: 'Stručný obsah',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'preceding',
      name: intl.formatMessage({
        id: 'ES__DELIVERED_DOCUMENTS_PROCESSED__TABLE__COLUMN__PRECEDING',
        defaultMessage: 'Předchází',
      }),
      width: 100,
      CellComponent: TableCells.BooleanCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'validation.valid',
      name: intl.formatMessage({
        id: 'ES__DELIVERED_DOCUMENTS_PROCESSED__TABLE__COLUMN__VALID',
        defaultMessage: 'Dokument je validní',
      }),
      width: 200,
      CellComponent: TableCells.BooleanCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'delivery.sender.label',
      filterDialogSpan: 2,
      sortkey: 'sender.label',
      filterkey: 'sender.label',
      name: intl.formatMessage({
        id: 'ES__DELIVERED_DOCUMENTS_PROCESSED__TABLE__COLUMN__DELIVERY_SENDER',
        defaultMessage: 'Odesílatel',
      }),
      width: 150,

      CellComponent: TableCells.TextCell,
      filterOperation: ApiFilterOperation.CONTAINS,
      FilterComponent: FilterTextCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'deliveryMethod',
      sortkey: 'deliveryMethod.name',
      filterkey: 'deliveryMethod.id',
      name: intl.formatMessage({
        id: 'ES__DELIVERED_DOCUMENTS_PROCESSED__TABLE__COLUMN__DELIVERY_METHOD',
        defaultMessage: 'Způsob doručení',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      FilterComponent:
        TableFilterCells.useFilterSelectCellFactory(useDeliveryMethods),
      valueMapper: TableCells.useSelectCellFactory(useDeliveryMethods),
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'agenda',
      sortkey: 'agenda.name',
      filterkey: 'agenda.id',
      name: intl.formatMessage({
        id: 'ES__DELIVERED_DOCUMENTS_PROCESSED__TABLE__COLUMN__AGENDA',
        defaultMessage: 'Agenda',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      FilterComponent: TableFilterCells.useFilterSelectCellFactory(useAgendas),
      valueMapper: TableCells.useSelectCellFactory(useAgendas),
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'esFile.judge',
      filterDialogSpan: 2,
      sortkey: 'esFile.judge.name',
      filterkey: 'esFile.judge.id',
      name: intl.formatMessage({
        id: 'ES__DELIVERED_DOCUMENTS_PROCESSED__TABLE__COLUMN__JUDGE',
        defaultMessage: 'Soudce',
      }),
      width: 200,
      valueMapper: autocompleteColumnMapper,
      CellComponent: TableCells.TextCell,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: () => useJudges({ include: ['DEACTIVATED'] }),
      }),
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'esFile.indexUserRole',
      filterDialogSpan: 2,
      sortkey: 'esFile.indexUserRole.name',
      filterkey: 'esFile.indexUserRole.id',
      name: intl.formatMessage({
        id: 'ES__DELIVERED_DOCUMENTS_PROCESSED__TABLE__COLUMN__INDEX',
        defaultMessage: 'Rejstřík',
      }),
      width: 200,
      valueMapper: autocompleteColumnMapper,
      CellComponent: TableCells.TextCell,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: useIndexes,
      }),
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
    },
  ];
}
